// components/AdminLayout.jsx — Dedicated Admin Shell
// Dark premium sidebar + top bar, fully decoupled from user DashboardLayout
import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    LayoutDashboard, Users, ShoppingBag, Briefcase, ClipboardList,
    Gavel, DollarSign, Tag, Gift, Wallet2, Mail, Bell,
    Star, TrendingUp, ShieldAlert, Phone, Crown, LogOut,
    ChevronLeft, ChevronRight, Menu, X, ExternalLink, BarChart3,
    Shield, GitBranch, UserCog, Terminal, LifeBuoy
} from 'lucide-react';

/* ─── Sidebar link definition ─── */
const NAV_SECTIONS = [
    {
        label: 'Overview',
        items: [
            { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
            { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
        ],
    },
    {
        label: 'Management',
        items: [
            { to: '/admin/users', icon: Users, label: 'Users' },
            { to: '/admin/onboarding', icon: LifeBuoy, label: 'Onboarding & Feedback' },
            { to: '/admin/orders', icon: ShoppingBag, label: 'Orders' },
            { to: '/admin/gigs', icon: Briefcase, label: 'Gigs' },
            { to: '/admin/job-requests', icon: ClipboardList, label: 'Job Requests' },
            { to: '/admin/disputes', icon: Gavel, label: 'Disputes' },
            { to: '/admin/verifications', icon: Shield, label: 'Verifications' },
            { to: '/admin/profile-change-requests', icon: UserCog, label: 'Profile Changes' },
            { to: '/admin/referrals', icon: GitBranch, label: 'Referrals' },
        ],
    },
    {
        label: 'Finance',
        items: [
            { to: '/admin/deposits', icon: DollarSign, label: 'Deposits' },
            { to: '/admin/offers', icon: Tag, label: 'Offers & Discounts' },
            { to: '/admin/payout-settings', icon: Wallet2, label: 'Payout Settings' },
        ],
    },
    {
        label: 'Engagement',
        items: [
            { to: '/admin/email-campaigns', icon: Mail, label: 'Email Campaigns' },
            { to: '/admin/email-alerts', icon: Bell, label: 'Email Alerts' },
            { to: '/admin/featured-management', icon: Star, label: 'Featured Listings' },
            { to: '/admin/growth', icon: TrendingUp, label: 'Growth Panel' },
        ],
    },
    {
        label: 'Safety',
        items: [
            { to: '/admin/safety', icon: ShieldAlert, label: 'Safety Dashboard' },
            { to: '/admin/call-settings', icon: Phone, label: 'Call Settings' },
        ],
    },
    {
        label: 'System',
        items: [
            { to: '/admin/logs', icon: Terminal, label: 'System Logs' },
        ],
    },
];

const SUPER_ADMIN_SECTION = {
    label: 'System',
    items: [
        { to: '/admin/super', icon: Crown, label: 'Super Admin Panel', superOnly: true },
    ],
};

/* ─── Sidebar Link ─── */
const SidebarLink = ({ item, pathname, collapsed, onClick }) => {
    const isActive = item.exact
        ? pathname === item.to
        : pathname.startsWith(item.to);

    return (
        <Link
            to={item.to}
            onClick={onClick}
            className={`
        group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150
        ${isActive
                    ? 'bg-white/10 text-white shadow-sm'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                }
        ${collapsed ? 'justify-center' : ''}
      `}
            title={collapsed ? item.label : undefined}
        >
            <item.icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-blue-400' : 'text-gray-500 group-hover:text-gray-400'}`} />
            {!collapsed && <span className="truncate">{item.label}</span>}
        </Link>
    );
};

/* ─── Main Layout ─── */
const AdminLayout = () => {
    const { user, logout, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const superAdmin = isSuperAdmin?.() ?? false;

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const sections = superAdmin
        ? [...NAV_SECTIONS, SUPER_ADMIN_SECTION]
        : NAV_SECTIONS;

    /* ─── Sidebar content (shared desktop / mobile) ─── */
    const SidebarContent = ({ mobile = false }) => (
        <div className="flex flex-col h-full">
            {/* Logo / Brand */}
            <div className={`flex items-center ${collapsed && !mobile ? 'justify-center' : 'gap-3'} px-4 py-5 border-b border-white/10`}>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-white" />
                </div>
                {(!collapsed || mobile) && (
                    <div className="min-w-0">
                        <p className="text-white font-bold text-sm leading-tight truncate">GigShip Admin</p>
                        <p className="text-[11px] text-gray-400 truncate">
                            {superAdmin ? '★ Super Admin' : 'Administrator'}
                        </p>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5 scrollbar-thin scrollbar-thumb-white/10">
                {sections.map((section) => (
                    <div key={section.label}>
                        {(!collapsed || mobile) && (
                            <p className="px-3 mb-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                {section.label}
                            </p>
                        )}
                        <div className="space-y-0.5">
                            {section.items.map((item) => (
                                <SidebarLink
                                    key={item.to}
                                    item={item}
                                    pathname={pathname}
                                    collapsed={collapsed && !mobile}
                                    onClick={mobile ? () => setMobileOpen(false) : undefined}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Footer — user info + logout */}
            <div className="border-t border-white/10 px-3 py-4 space-y-2">
                {(!collapsed || mobile) && (
                    <div className="flex items-center gap-3 px-3 mb-2">
                        <img
                            src={user?.profilePicture || '/default-avatar.png'}
                            alt={user?.username}
                            className="h-8 w-8 rounded-full object-cover ring-2 ring-white/20"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                            <p className="text-[11px] text-gray-400 truncate">{user?.email}</p>
                        </div>
                    </div>
                )}
                <Link
                    to="/"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-colors ${collapsed && !mobile ? 'justify-center' : ''}`}
                    title={collapsed ? 'Public Site' : undefined}
                >
                    <ExternalLink className="h-4 w-4 flex-shrink-0" />
                    {(!collapsed || mobile) && <span>Public Site</span>}
                </Link>
                <button
                    onClick={handleLogout}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors ${collapsed && !mobile ? 'justify-center' : ''}`}
                    title={collapsed ? 'Logout' : undefined}
                >
                    <LogOut className="h-4 w-4 flex-shrink-0" />
                    {(!collapsed || mobile) && <span>Logout</span>}
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {/* ─── Desktop Sidebar ─── */}
            <aside
                className={`
          hidden lg:flex flex-col bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950
          transition-all duration-300 ease-in-out flex-shrink-0
          ${collapsed ? 'w-[72px]' : 'w-64'}
        `}
            >
                <SidebarContent />
                {/* Collapse toggle */}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="absolute bottom-20 -right-3 z-20 hidden lg:flex h-6 w-6 items-center justify-center rounded-full bg-gray-700 border-2 border-gray-900 text-gray-400 hover:text-white hover:bg-gray-600 transition-colors"
                    style={{ left: collapsed ? '60px' : '248px' }}
                >
                    {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
                </button>
            </aside>

            {/* ─── Mobile Overlay ─── */}
            {mobileOpen && (
                <div className="fixed inset-0 z-40 lg:hidden">
                    <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
                    <aside className="fixed inset-y-0 left-0 w-72 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 z-50 shadow-2xl">
                        <button
                            onClick={() => setMobileOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <SidebarContent mobile />
                    </aside>
                </div>
            )}

            {/* ─── Main content area ─── */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top bar */}
                <header className="bg-white border-b border-gray-200 px-4 lg:px-6 h-14 flex items-center justify-between flex-shrink-0 z-10">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                        <div className="hidden sm:flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${superAdmin ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                                {superAdmin ? '★ Super Admin' : 'Admin'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/"
                            className="hidden sm:flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
                        >
                            <ExternalLink className="h-4 w-4" />
                            <span>Visit Site</span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <img
                                src={user?.profilePicture || '/default-avatar.png'}
                                alt={user?.username}
                                className="h-8 w-8 rounded-full object-cover"
                            />
                            <span className="hidden md:block text-sm font-medium text-gray-700">{user?.username}</span>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
